'use client';

/**
 * UserStuckFundsSection Component
 *
 * Displays all user positions with recovery options.
 * This is where Chewy saves the day!
 */
import { c as _c } from "react/compiler-runtime";
import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Button, Chip } from '@heroui/react';
import { RefreshCw, Shovel, CheckCircle, PartyPopper, DollarSign } from 'lucide-react';
import Image from 'next/image';
import { useChainId } from 'wagmi';
import Col from '@/components/Layout/Flex/Col';
import Row from '@/components/Layout/Flex/Row';
import { useTokenPricing } from '@/hooks/portfolio/useTokenPricing';
import { useTotalPositionsValue } from '@/hooks/masterchef/useTotalPositionsValue';
import type { UnderlyingAsset } from '@/lib/masterchef-cache/types';
import UserStuckFundsCard from './UserStuckFundsCard';
import RecoveryModal from './RecoveryModal';
import TipCreator from './TipCreator';
interface StuckFundsPosition {
  protocol: {
    name: string;
    slug: string;
    status: string;
    statusReason?: string;
  };
  contract: {
    contractId: string;
    label: string;
    contractAddress: `0x${string}`;
    chainId: number;
  };
  poolId: number;
  lpTokenAddress: `0x${string}`;
  lpTokenSymbol?: string;
  lpTokenDecimals: number;
  stakedAmount: bigint;
  stakedAmountFormatted: string;
  pendingRewards?: bigint;
  underlyingAssets: UnderlyingAsset[];
  withdrawConfig: {
    standardWithdrawAbi: any;
    emergencyWithdrawAbi?: any;
    recommendation: 'standard' | 'emergency';
  };
}
interface UserStuckFundsSectionProps {
  positions: StuckFundsPosition[];
  isScanning: boolean;
  onRescan: () => void;
  /** Address being scanned (for display purposes) */
  scannedAddress?: string;
  /** Whether we're checking a friend's wallet */
  isCheckingFriend?: boolean;
}
export default function UserStuckFundsSection(t0) {
  const $ = _c(118);
  const {
    positions,
    isScanning,
    onRescan,
    scannedAddress,
    isCheckingFriend
  } = t0;
  const chainId = useChainId();
  const [selectedPosition, setSelectedPosition] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Set();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [recoveredPositions, setRecoveredPositions] = useState(t1);
  let t2;
  if ($[1] !== positions) {
    const uniqueTokens = new Map();
    for (const position of positions) {
      for (const asset of position.underlyingAssets) {
        const key = asset.address.toLowerCase();
        if (!uniqueTokens.has(key)) {
          uniqueTokens.set(key, {
            address: asset.address,
            symbol: asset.symbol,
            decimals: asset.decimals
          });
        }
      }
    }
    t2 = Array.from(uniqueTokens.values());
    $[1] = positions;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const tokensForPricing = t2;
  const t3 = tokensForPricing as any;
  const t4 = tokensForPricing.length > 0;
  let t5;
  if ($[3] !== chainId || $[4] !== t3 || $[5] !== t4) {
    t5 = {
      tokens: t3,
      chainId,
      enabled: t4
    };
    $[3] = chainId;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const {
    prices,
    isLoading: isPricingLoading
  } = useTokenPricing(t5);
  let t6;
  if ($[7] !== positions || $[8] !== recoveredPositions) {
    let t7;
    if ($[10] !== recoveredPositions) {
      t7 = p => !recoveredPositions.has(`${p.contract.contractAddress}-${p.poolId}`);
      $[10] = recoveredPositions;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    t6 = positions.filter(t7);
    $[7] = positions;
    $[8] = recoveredPositions;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = positions.length > 0 && !isPricingLoading;
  let t8;
  if ($[12] !== chainId || $[13] !== prices || $[14] !== t6 || $[15] !== t7) {
    t8 = {
      positions: t6,
      prices,
      chainId,
      enabled: t7
    };
    $[12] = chainId;
    $[13] = prices;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const {
    totalValueUsd,
    isLoading: isTotalLoading
  } = useTotalPositionsValue(t8);
  const getPositionKey = _temp;
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = position_0 => {
      setRecoveredPositions(prev => new Set([...prev, getPositionKey(position_0)]));
      setSelectedPosition(null);
    };
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const handleRecoverySuccess = t9;
  let T0;
  let T1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  if ($[18] !== isCheckingFriend || $[19] !== isPricingLoading || $[20] !== isScanning || $[21] !== isTotalLoading || $[22] !== onRescan || $[23] !== positions || $[24] !== prices || $[25] !== recoveredPositions || $[26] !== scannedAddress || $[27] !== totalValueUsd) {
    t16 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let t17;
      if ($[37] !== recoveredPositions) {
        t17 = p_0 => !recoveredPositions.has(getPositionKey(p_0));
        $[37] = recoveredPositions;
        $[38] = t17;
      } else {
        t17 = $[38];
      }
      const activePositions = positions.filter(t17);
      const hasRecoveredAll = positions.length > 0 && activePositions.length === 0;
      if (hasRecoveredAll) {
        let t18;
        let t19;
        if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
          t18 = {
            opacity: 0,
            scale: 0.9
          };
          t19 = {
            opacity: 1,
            scale: 1
          };
          $[39] = t18;
          $[40] = t19;
        } else {
          t18 = $[39];
          t19 = $[40];
        }
        let t20;
        let t21;
        if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
          t20 = <motion.div animate={{
            scale: [1, 1.1, 1],
            rotate: [0, 5, -5, 0]
          }} transition={{
            duration: 2,
            repeat: Infinity
          }}><PartyPopper className="w-20 h-20 text-success" /></motion.div>;
          t21 = <h3 className="text-2xl font-bold text-success text-center">All Funds Rescued!</h3>;
          $[41] = t20;
          $[42] = t21;
        } else {
          t20 = $[41];
          t21 = $[42];
        }
        let t22;
        if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
          t22 = <p className="text-foreground-500 text-center max-w-md">Chewy has successfully dug up all your buried treasure!<br />Your tokens are now safe.</p>;
          $[43] = t22;
        } else {
          t22 = $[43];
        }
        let t23;
        let t24;
        if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
          t23 = <div className="relative w-32 h-32"><Image src="/images/default/dig.svg" alt="Chewy celebrating" fill={true} className="object-contain" /></div>;
          t24 = <TipCreator compact={true} className="w-full max-w-lg" />;
          $[44] = t23;
          $[45] = t24;
        } else {
          t23 = $[44];
          t24 = $[45];
        }
        let t25;
        if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
          t25 = <RefreshCw className="w-4 h-4" />;
          $[46] = t25;
        } else {
          t25 = $[46];
        }
        let t26;
        if ($[47] !== onRescan) {
          t26 = <motion.div initial={t18} animate={t19} className="flex flex-col items-center justify-center py-16 gap-6 bg-success/10 rounded-2xl border border-success/30">{t20}{t21}{t22}{t23}{t24}<Button color="default" variant="flat" onPress={onRescan}>{t25}Scan Again</Button></motion.div>;
          $[47] = onRescan;
          $[48] = t26;
        } else {
          t26 = $[48];
        }
        t16 = t26;
        break bb0;
      }
      if (positions.length === 0 && !isScanning) {
        let t18;
        if ($[49] !== scannedAddress) {
          t18 = scannedAddress ? `${scannedAddress.slice(0, 6)}...${scannedAddress.slice(-4)}` : "";
          $[49] = scannedAddress;
          $[50] = t18;
        } else {
          t18 = $[50];
        }
        const addressDisplay = t18;
        let t19;
        let t20;
        if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
          t19 = {
            opacity: 0,
            scale: 0.9
          };
          t20 = {
            opacity: 1,
            scale: 1
          };
          $[51] = t19;
          $[52] = t20;
        } else {
          t19 = $[51];
          t20 = $[52];
        }
        let t21;
        let t22;
        if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
          t21 = <CheckCircle className="w-20 h-20 text-success" />;
          t22 = <h3 className="text-2xl font-bold text-success text-center">No Stuck Funds Found!</h3>;
          $[53] = t21;
          $[54] = t22;
        } else {
          t21 = $[53];
          t22 = $[54];
        }
        let t23;
        if ($[55] !== addressDisplay || $[56] !== isCheckingFriend) {
          t23 = <p className="text-foreground-500 text-center max-w-md">{isCheckingFriend ? <>Great news! Chewy couldn't find any tokens buried in abandoned protocols for{" "}<span className="font-mono text-primary font-semibold">{addressDisplay}</span>.</> : <>Great news! Chewy couldn't find any of your tokens buried in abandoned protocols on this chain.</>}</p>;
          $[55] = addressDisplay;
          $[56] = isCheckingFriend;
          $[57] = t23;
        } else {
          t23 = $[57];
        }
        let t24;
        if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
          t24 = <RefreshCw className="w-4 h-4" />;
          $[58] = t24;
        } else {
          t24 = $[58];
        }
        let t25;
        if ($[59] !== onRescan) {
          t25 = <Button color="default" variant="flat" onPress={onRescan}>{t24}Scan Again</Button>;
          $[59] = onRescan;
          $[60] = t25;
        } else {
          t25 = $[60];
        }
        let t26;
        if ($[61] !== t23 || $[62] !== t25) {
          t26 = <motion.div initial={t19} animate={t20} className="flex flex-col items-center justify-center py-16 gap-6 bg-success/10 rounded-2xl border border-success/30">{t21}{t22}{t23}{t25}</motion.div>;
          $[61] = t23;
          $[62] = t25;
          $[63] = t26;
        } else {
          t26 = $[63];
        }
        t16 = t26;
        break bb0;
      }
      let t18;
      if ($[64] !== scannedAddress) {
        t18 = scannedAddress ? `${scannedAddress.slice(0, 6)}...${scannedAddress.slice(-4)}` : "";
        $[64] = scannedAddress;
        $[65] = t18;
      } else {
        t18 = $[65];
      }
      const addressDisplay_0 = t18;
      T1 = Col;
      t13 = "gap-6";
      let t19;
      if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
        t19 = <motion.div animate={{
          rotate: [0, 15, -15, 0]
        }} transition={{
          duration: 1,
          repeat: Infinity,
          repeatDelay: 2
        }}><Shovel className="w-6 h-6 text-warning" /></motion.div>;
        $[66] = t19;
      } else {
        t19 = $[66];
      }
      let t20;
      if ($[67] !== addressDisplay_0 || $[68] !== isCheckingFriend) {
        t20 = isCheckingFriend ? <>Buried Treasure for{" "}<span className="text-secondary">{addressDisplay_0}</span></> : "Your Buried Treasure";
        $[67] = addressDisplay_0;
        $[68] = isCheckingFriend;
        $[69] = t20;
      } else {
        t20 = $[69];
      }
      let t21;
      if ($[70] !== t20) {
        t21 = <h2 className="text-xl font-bold">{t20}</h2>;
        $[70] = t20;
        $[71] = t21;
      } else {
        t21 = $[71];
      }
      let t22;
      if ($[72] !== recoveredPositions.size) {
        t22 = recoveredPositions.size > 0 && <Chip size="sm" color="success" variant="flat" className="ml-2">{recoveredPositions.size} rescued</Chip>;
        $[72] = recoveredPositions.size;
        $[73] = t22;
      } else {
        t22 = $[73];
      }
      const t23 = <div>{t21}<p className="text-sm text-foreground-500">{activePositions.length} position{activePositions.length !== 1 ? "s" : ""} found{t22}</p></div>;
      let t24;
      if ($[74] !== t23) {
        t24 = <div className="flex items-center gap-3">{t19}{t23}</div>;
        $[74] = t23;
        $[75] = t24;
      } else {
        t24 = $[75];
      }
      let t25;
      if ($[76] !== isPricingLoading || $[77] !== isTotalLoading || $[78] !== totalValueUsd) {
        t25 = (isTotalLoading || isPricingLoading || totalValueUsd !== null) && <div className="flex items-center gap-2 px-4 py-2 bg-success/10 rounded-xl border border-success/30"><DollarSign className="w-5 h-5 text-success" /><div className="text-right"><p className="text-xs text-foreground-500">Total Value</p>{isTotalLoading || isPricingLoading ? <div className="h-6 w-20 bg-content2 rounded animate-pulse" /> : <p className="text-lg font-bold text-success">{totalValueUsd !== null ? `$${totalValueUsd.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}` : "--"}</p>}</div></div>;
        $[76] = isPricingLoading;
        $[77] = isTotalLoading;
        $[78] = totalValueUsd;
        $[79] = t25;
      } else {
        t25 = $[79];
      }
      let t26;
      if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
        t26 = <RefreshCw className="w-4 h-4" />;
        $[80] = t26;
      } else {
        t26 = $[80];
      }
      let t27;
      if ($[81] !== isScanning || $[82] !== onRescan) {
        t27 = <Button color="default" variant="flat" size="sm" isLoading={isScanning} onPress={onRescan}>{t26}Rescan</Button>;
        $[81] = isScanning;
        $[82] = onRescan;
        $[83] = t27;
      } else {
        t27 = $[83];
      }
      let t28;
      if ($[84] !== t25 || $[85] !== t27) {
        t28 = <div className="flex items-center gap-4">{t25}{t27}</div>;
        $[84] = t25;
        $[85] = t27;
        $[86] = t28;
      } else {
        t28 = $[86];
      }
      if ($[87] !== t24 || $[88] !== t28) {
        t14 = <Row className="justify-between items-center flex-wrap gap-4">{t24}{t28}</Row>;
        $[87] = t24;
        $[88] = t28;
        $[89] = t14;
      } else {
        t14 = $[89];
      }
      if ($[90] !== isScanning) {
        t15 = isScanning && <div className="flex items-center justify-center gap-3 py-8 bg-warning/10 rounded-xl border border-warning/30"><motion.div animate={{
            rotate: 360
          }} transition={{
            duration: 1,
            repeat: Infinity,
            ease: "linear"
          }}><RefreshCw className="w-6 h-6 text-warning" /></motion.div><span className="text-warning font-medium">Chewy is digging...</span></div>;
        $[90] = isScanning;
        $[91] = t15;
      } else {
        t15 = $[91];
      }
      T0 = AnimatePresence;
      t12 = "popLayout";
      t10 = "grid grid-cols-1 md:grid-cols-2 gap-4";
      let t29;
      if ($[92] !== isCheckingFriend || $[93] !== isPricingLoading || $[94] !== prices || $[95] !== scannedAddress) {
        t29 = position_1 => <motion.div key={getPositionKey(position_1)} layout={true} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.8
        }}><UserStuckFundsCard position={position_1} onRecover={() => setSelectedPosition(position_1)} isCheckingFriend={isCheckingFriend} scannedAddress={scannedAddress} tokenPrices={prices} isPricingLoading={isPricingLoading} /></motion.div>;
        $[92] = isCheckingFriend;
        $[93] = isPricingLoading;
        $[94] = prices;
        $[95] = scannedAddress;
        $[96] = t29;
      } else {
        t29 = $[96];
      }
      t11 = activePositions.map(t29);
    }
    $[18] = isCheckingFriend;
    $[19] = isPricingLoading;
    $[20] = isScanning;
    $[21] = isTotalLoading;
    $[22] = onRescan;
    $[23] = positions;
    $[24] = prices;
    $[25] = recoveredPositions;
    $[26] = scannedAddress;
    $[27] = totalValueUsd;
    $[28] = T0;
    $[29] = T1;
    $[30] = t10;
    $[31] = t11;
    $[32] = t12;
    $[33] = t13;
    $[34] = t14;
    $[35] = t15;
    $[36] = t16;
  } else {
    T0 = $[28];
    T1 = $[29];
    t10 = $[30];
    t11 = $[31];
    t12 = $[32];
    t13 = $[33];
    t14 = $[34];
    t15 = $[35];
    t16 = $[36];
  }
  if (t16 !== Symbol.for("react.early_return_sentinel")) {
    return t16;
  }
  let t17;
  if ($[97] !== t10 || $[98] !== t11) {
    t17 = <div className={t10}>{t11}</div>;
    $[97] = t10;
    $[98] = t11;
    $[99] = t17;
  } else {
    t17 = $[99];
  }
  let t18;
  if ($[100] !== T0 || $[101] !== t12 || $[102] !== t17) {
    t18 = <T0 mode={t12}>{t17}</T0>;
    $[100] = T0;
    $[101] = t12;
    $[102] = t17;
    $[103] = t18;
  } else {
    t18 = $[103];
  }
  const t19 = selectedPosition !== null;
  let t20;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => setSelectedPosition(null);
    $[104] = t20;
  } else {
    t20 = $[104];
  }
  let t21;
  if ($[105] !== selectedPosition) {
    t21 = () => selectedPosition && handleRecoverySuccess(selectedPosition);
    $[105] = selectedPosition;
    $[106] = t21;
  } else {
    t21 = $[106];
  }
  let t22;
  if ($[107] !== selectedPosition || $[108] !== t19 || $[109] !== t21) {
    t22 = <RecoveryModal position={selectedPosition} isOpen={t19} onClose={t20} onSuccess={t21} />;
    $[107] = selectedPosition;
    $[108] = t19;
    $[109] = t21;
    $[110] = t22;
  } else {
    t22 = $[110];
  }
  let t23;
  if ($[111] !== T1 || $[112] !== t13 || $[113] !== t14 || $[114] !== t15 || $[115] !== t18 || $[116] !== t22) {
    t23 = <T1 className={t13}>{t14}{t15}{t18}{t22}</T1>;
    $[111] = T1;
    $[112] = t13;
    $[113] = t14;
    $[114] = t15;
    $[115] = t18;
    $[116] = t22;
    $[117] = t23;
  } else {
    t23 = $[117];
  }
  return t23;
}
function _temp(pos) {
  return `${pos.contract.contractAddress}-${pos.poolId}`;
}